/**
 * Agente: creazione preventivo con catalogo + volume-pricing automatico.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAgentAuth } from '../../contexts/AgentAuthContext';
import AgentLayout from './AgentLayout';
import { Plus, Trash2, ArrowLeft, Sparkles, Loader2, ShoppingCart, Palette } from 'lucide-react';
import GraphicRequestModal from './GraphicRequestModal';

const API = process.env.REACT_APP_BACKEND_URL;

const eur = (v) => Number(v || 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });

const AgentQuoteNew = () => {
  const { agentToken } = useAgentAuth();
  const navigate = useNavigate();
  const { quoteId } = useParams();
  const isEdit = !!quoteId;
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState('');
  const [catalog, setCatalog] = useState({ liquors: [], formats: [], discount_tiers: [] });
  const [items, setItems] = useState([]);
  const [notes, setNotes] = useState('');
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [paymentMethodId, setPaymentMethodId] = useState('');
  const [validityDays, setValidityDays] = useState(30);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [loadingExisting, setLoadingExisting] = useState(isEdit);
  const [graphicModal, setGraphicModal] = useState(null); // {itemIndex, defaultProductName, existingRequestId}
  const [graphicByIndex, setGraphicByIndex] = useState({}); // {index: {id, status}}

  useEffect(() => {
    if (!agentToken) return;
    (async () => {
      const [cRes, catRes, pmRes] = await Promise.all([
        fetch(`${API}/api/agent/customers`, { headers: { Authorization: `Bearer ${agentToken}` } }),
        fetch(`${API}/api/agent/catalog`, { headers: { Authorization: `Bearer ${agentToken}` } }),
        fetch(`${API}/api/agent/payment-methods`, { headers: { Authorization: `Bearer ${agentToken}` } }),
      ]);
      const c = await cRes.json();
      const cat = await catRes.json();
      const pm = await pmRes.json();
      setCustomers(c.data || []);
      setCatalog({ liquors: cat.liquors || [], formats: cat.formats || [], discount_tiers: cat.discount_tiers || [] });
      const methods = pm.data || [];
      setPaymentMethods(methods);
      if (methods.length > 0 && !isEdit) setPaymentMethodId(methods[0].id);

      if (isEdit) {
        try {
          const qRes = await fetch(`${API}/api/agent/quotes/${quoteId}`, {
            headers: { Authorization: `Bearer ${agentToken}` },
          });
          if (qRes.ok) {
            const qj = await qRes.json();
            const q = qj.data || {};
            setCustomerId(q.customer_data?.customer_id || '');
            setNotes(q.admin_notes || '');
            setValidityDays(q.validity_days || 30);
            const loadedItems = (q.items || []).map((it, idx) => ({
              id: Date.now() + idx,
              liquor_id: '',
              liquor_name: '',
              format_size_cl: Number((it.format || '50cl').replace('cl', '')) || 50,
              quantity: it.quantity || 0,
              unit_price: it.unit_price || 0,
              tier_label: '',
              discount_percent: it.discount_percent || 0,
              alcohol_content: it.alcohol_content || null,
              description: it.description || '',
              details: it.details || '',
              loading: false,
            }));
            setItems(loadedItems);

            // Carica richieste grafiche esistenti per questo preventivo
            try {
              const grRes = await fetch(`${API}/api/agent/graphic-requests?quote_id=${quoteId}`, {
                headers: { Authorization: `Bearer ${agentToken}` },
              });
              if (grRes.ok) {
                const gj = await grRes.json();
                const map = {};
                (gj.data || []).forEach((g) => { map[g.quote_item_index] = { id: g.id, status: g.status }; });
                setGraphicByIndex(map);
              }
            } catch { /* ignore */ }
          }
        } catch { /* ignore */ }
        setLoadingExisting(false);
      }
    })();
    // eslint-disable-next-line
  }, [agentToken, quoteId]);

  const addItem = () => {
    const defaultLiquor = catalog.liquors[0];
    const defaultFmt = catalog.formats.find((f) => Number(f.size_cl) === 50) || catalog.formats[0];
    setItems((prev) => [
      ...prev,
      {
        id: Date.now(),
        liquor_id: defaultLiquor?.id || '',
        liquor_name: defaultLiquor?.name || '',
        format_size_cl: Number(defaultFmt?.size_cl) || 50,
        quantity: 100,
        unit_price: 0,
        tier_label: '',
        discount_percent: 0,
        alcohol_content: defaultLiquor?.alcoholContent || defaultLiquor?.alcohol_content || null,
        details: '',
        loading: true,
      },
    ]);
  };

  const updateLineField = (id, patch) =>
    setItems((prev) => prev.map((x) => (x.id === id ? { ...x, ...patch } : x)));

  const removeItem = (id) => setItems((prev) => prev.filter((x) => x.id !== id));

  const recomputeLine = async (id, patch) => {
    setItems((prev) => prev.map((x) => (x.id === id ? { ...x, ...patch, loading: true } : x)));
    const line = { ...(items.find((x) => x.id === id) || {}), ...patch };
    if (!line.liquor_id || !line.format_size_cl || !line.quantity) return;
    try {
      const res = await fetch(`${API}/api/agent/catalog/price`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${agentToken}` },
        body: JSON.stringify({ liquor_id: line.liquor_id, format_size_cl: Number(line.format_size_cl), quantity: Number(line.quantity) }),
      });
      const j = await res.json();
      if (res.ok) {
        setItems((prev) => prev.map((x) => (x.id === id ? {
          ...x, ...patch,
          liquor_name: j.liquor_name,
          unit_price: j.unit_price,
          tier_label: j.tier_label,
          discount_percent: j.discount_percent,
          alcohol_content: j.alcohol_content,
          loading: false,
        } : x)));
      } else {
        setItems((prev) => prev.map((x) => (x.id === id ? { ...x, ...patch, loading: false } : x)));
      }
    } catch {
      setItems((prev) => prev.map((x) => (x.id === id ? { ...x, ...patch, loading: false } : x)));
    }
  };

  // Auto-compute la prima riga appena aggiunta
  useEffect(() => {
    const uncomputed = items.find((x) => x.loading && x.liquor_id);
    if (uncomputed) recomputeLine(uncomputed.id, {});
    // eslint-disable-next-line
  }, [items.length]);

  const total = useMemo(() => items.reduce((s, x) => s + (Number(x.unit_price) || 0) * (Number(x.quantity) || 0), 0), [items]);
  const totalBottles = useMemo(() => items.reduce((s, x) => s + (Number(x.quantity) || 0), 0), [items]);
  const shippingCost = useMemo(() => {
    if (totalBottles <= 0) return 0;
    const tier = (catalog.discount_tiers || []).find(t => {
      const mn = Number(t.min_quantity || 0);
      const mx = t.max_quantity == null ? null : Number(t.max_quantity);
      return totalBottles >= mn && (mx == null || totalBottles <= mx);
    });
    return tier ? Number(tier.shipping_cost || 0) : 0;
  }, [totalBottles, catalog.discount_tiers]);
  const taxable = total + shippingCost;
  const vat = taxable * 0.22;
  const grandTotal = taxable + vat;

  const submit = async () => {
    if (!customerId) { setError('Seleziona un cliente'); return; }
    if (items.length === 0) { setError('Aggiungi almeno un prodotto'); return; }
    setError(''); setSaving(true);
    try {
      const payload = {
        customer_id: customerId,
        validity_days: Number(validityDays) || 30,
        admin_notes: notes || null,
        payment_method_id: paymentMethodId || null,
        items: items.map((x) => {
          const liquor = catalog.liquors.find((l) => l.id === x.liquor_id);
          return {
            liquor_id: x.liquor_id || undefined,
            description: x.description || `${liquor?.name || 'Liquore'} personalizzato ${x.format_size_cl}cl`,
            details: x.details || null,
            format: `${x.format_size_cl}cl`,
            quantity: Number(x.quantity),
            unit_price: Number(x.unit_price),
            discount_percent: Number(x.discount_percent) || 0,
            alcohol_content: x.alcohol_content,
          };
        }),
      };
      const url = isEdit ? `${API}/api/agent/quotes/${quoteId}` : `${API}/api/agent/quotes`;
      const method = isEdit ? 'PUT' : 'POST';
      // In edit non serve customer_id nel payload
      if (isEdit) delete payload.customer_id;
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${agentToken}` },
        body: JSON.stringify(payload),
      });
      const j = await res.json();
      if (res.ok) {
        navigate('/agent/quotes');
      } else {
        setError(j.detail || 'Errore creazione preventivo');
      }
    } finally { setSaving(false); }
  };

  return (
    <AgentLayout>
      <div className="mb-6 flex items-center gap-3">
        <button onClick={() => navigate('/agent/quotes')} className="text-gray-500 hover:text-gray-900">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{isEdit ? 'Modifica preventivo' : 'Nuovo preventivo'}</h1>
          <p className="text-sm text-gray-600 mt-1">
            {isEdit
              ? 'Aggiorna righe, note e metodo di pagamento. Il preventivo mantiene lo stesso numero.'
              : 'Configura righe da catalogo: il prezzo si aggiorna in automatico in base alla quantità.'}
          </p>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-purple-100 p-6 space-y-6" data-testid="agent-quote-new-form">
        {/* Cliente */}
        <div>
          <label className="text-xs font-semibold text-gray-700 uppercase">Cliente</label>
          <select value={customerId} onChange={(e) => setCustomerId(e.target.value)}
            data-testid="quote-customer-select"
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm">
            <option value="">— Seleziona cliente dal tuo portafoglio —</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.companyName || `${c.firstName} ${c.lastName}`} · {c.email}
              </option>
            ))}
          </select>
          {customers.length === 0 && (
            <div className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
              Nessun cliente nel tuo portafoglio. <a href="/agent/customers" className="underline">Crea un cliente</a> prima.
            </div>
          )}
        </div>

        {/* Righe */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-gray-900">Prodotti</h3>
            <button type="button" onClick={addItem} disabled={catalog.liquors.length === 0} data-testid="btn-add-item"
              className="inline-flex items-center gap-1 px-3 py-1.5 bg-purple-100 text-purple-700 rounded-md text-sm hover:bg-purple-200">
              <Plus className="w-4 h-4" /> Aggiungi prodotto
            </button>
          </div>
          {items.length === 0 ? (
            <div className="text-center py-8 text-gray-500 text-sm border-2 border-dashed border-gray-200 rounded-lg">
              <ShoppingCart className="w-8 h-8 mx-auto text-gray-300 mb-2" />
              Nessun prodotto. Clicca &quot;Aggiungi prodotto&quot;.
            </div>
          ) : (
            <div className="space-y-2">
              {items.map((it, idx) => (
                <div key={it.id} className="bg-purple-50/40 border border-purple-100 rounded-lg p-3 space-y-2" data-testid={`quote-line-${it.id}`}>
                  <div className="grid grid-cols-12 gap-2 items-center">
                    <div className="col-span-4">
                      <label className="text-[10px] font-semibold text-gray-500 uppercase">Liquore</label>
                      <select value={it.liquor_id} onChange={(e) => recomputeLine(it.id, { liquor_id: e.target.value })}
                        className="mt-0.5 w-full px-2 py-1.5 border border-gray-300 rounded text-xs">
                        {catalog.liquors.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                      </select>
                    </div>
                    <div className="col-span-2">
                      <label className="text-[10px] font-semibold text-gray-500 uppercase">Formato</label>
                      <select value={it.format_size_cl} onChange={(e) => recomputeLine(it.id, { format_size_cl: Number(e.target.value) })}
                        className="mt-0.5 w-full px-2 py-1.5 border border-gray-300 rounded text-xs">
                        {catalog.formats.map((f) => <option key={f.size_cl} value={f.size_cl}>{f.size_cl}cl</option>)}
                      </select>
                    </div>
                    <div className="col-span-2">
                      <label className="text-[10px] font-semibold text-gray-500 uppercase">Quantità</label>
                      <input type="number" min="1" value={it.quantity}
                        onChange={(e) => recomputeLine(it.id, { quantity: Number(e.target.value) })}
                        className="mt-0.5 w-full px-2 py-1.5 border border-gray-300 rounded text-xs" />
                    </div>
                    <div className="col-span-2 text-right">
                      <label className="text-[10px] font-semibold text-gray-500 uppercase">Prezzo unit.</label>
                      <div className="text-sm font-semibold text-purple-700 tabular-nums">
                        {it.loading ? <Loader2 className="w-3 h-3 inline animate-spin" /> : eur(it.unit_price)}
                      </div>
                      {it.tier_label && <div className="text-[9px] text-gray-500">tier: {it.tier_label} (-{it.discount_percent}%)</div>}
                    </div>
                    <div className="col-span-1 text-right text-sm tabular-nums font-bold text-gray-900">
                      {eur((it.unit_price || 0) * (it.quantity || 0))}
                    </div>
                    <div className="col-span-1 text-right">
                      <button type="button" onClick={() => removeItem(it.id)}
                        className="p-1 rounded hover:bg-red-100 text-red-600" data-testid={`btn-remove-${it.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Descrizione / commento riga */}
                  <div>
                    <label className="text-[10px] font-semibold text-gray-500 uppercase">
                      Descrizione riga (opzionale)
                    </label>
                    <textarea
                      rows={2}
                      value={it.details || ''}
                      onChange={(e) => updateLineField(it.id, { details: e.target.value })}
                      placeholder="Es. Etichetta oro con nome nozze, tappo T-cork nero, ricetta con genziana e china…"
                      className="mt-0.5 w-full px-2 py-1.5 border border-gray-300 rounded text-xs resize-y"
                      data-testid={`quote-line-details-${it.id}`}
                    />
                    <div className="text-[9px] text-gray-500 mt-0.5">
                      Comparirà sotto il nome prodotto nel PDF preventivo (corsivo grigio).
                    </div>
                  </div>

                  {/* Richiesta bozza grafica */}
                  <div className="flex items-center justify-between pt-1 border-t border-purple-100">
                    <div className="text-[10px] text-gray-500">
                      {graphicByIndex[idx] ? (
                        <span className="inline-flex items-center gap-1">
                          🎨 Bozza grafica:
                          <span className="uppercase font-mono px-1.5 py-0.5 rounded bg-purple-100 text-purple-700">
                            {graphicByIndex[idx].status}
                          </span>
                        </span>
                      ) : (
                        'Nessuna richiesta grafica per questa riga.'
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        if (!isEdit) {
                          alert('Salva prima il preventivo con "Crea preventivo", poi torna qui per richiedere la bozza grafica.');
                          return;
                        }
                        const liquor = catalog.liquors.find((l) => l.id === it.liquor_id);
                        setGraphicModal({
                          itemIndex: idx,
                          defaultProductName: liquor?.name ? `${liquor.name} ${it.format_size_cl}cl` : '',
                          existingRequestId: graphicByIndex[idx]?.id,
                        });
                      }}
                      data-testid={`btn-request-graphic-${it.id}`}
                      className="inline-flex items-center gap-1 px-2 py-1 border border-purple-300 text-purple-700 rounded text-xs hover:bg-purple-100"
                    >
                      <Palette className="w-3 h-3" />
                      {graphicByIndex[idx] ? 'Modifica bozza' : 'Richiedi bozza grafica'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Note + pagamento */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-semibold text-gray-700 uppercase">Validità (giorni)</label>
            <input type="number" min="1" value={validityDays} onChange={(e) => setValidityDays(e.target.value)}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-700 uppercase">Metodo di pagamento</label>
            <select value={paymentMethodId} onChange={(e) => setPaymentMethodId(e.target.value)}
              data-testid="payment-method-select"
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm">
              {paymentMethods.length === 0 ? (
                <option value="">Nessun metodo configurato — contatta admin</option>
              ) : (
                paymentMethods.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}{m.description ? ` — ${m.description}` : ''}
                  </option>
                ))
              )}
            </select>
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-700 uppercase">Note interne (opzionale)</label>
          <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
            placeholder="Contesto trattativa, richieste speciali del cliente…"
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
        </div>

        {/* Totali */}
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 grid grid-cols-4 gap-3 text-sm">
          <div>
            <div className="text-xs text-purple-600 font-semibold">Imponibile</div>
            <div className="text-lg font-bold text-purple-900 tabular-nums" data-testid="totals-subtotal">{eur(total)}</div>
          </div>
          <div>
            <div className="text-xs text-orange-600 font-semibold flex items-center gap-1">
              Spedizione
              {totalBottles > 0 && (
                <span className="text-[10px] text-gray-500 font-normal">({totalBottles} bot.)</span>
              )}
            </div>
            <div className="text-lg font-bold text-orange-700 tabular-nums" data-testid="totals-shipping">{eur(shippingCost)}</div>
          </div>
          <div>
            <div className="text-xs text-purple-600 font-semibold">IVA 22%</div>
            <div className="text-lg font-bold text-purple-900 tabular-nums">{eur(vat)}</div>
          </div>
          <div>
            <div className="text-xs text-purple-600 font-semibold">Totale</div>
            <div className="text-lg font-bold text-purple-900 tabular-nums" data-testid="totals-grand">{eur(grandTotal)}</div>
          </div>
        </div>

        {error && <div className="bg-red-50 border border-red-200 rounded-md px-3 py-2 text-sm text-red-700">{error}</div>}

        <div className="flex justify-end gap-2">
          <button type="button" onClick={() => navigate('/agent/quotes')} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md text-sm">Annulla</button>
          <button type="button" onClick={submit} disabled={saving || items.length === 0 || (!isEdit && !customerId)}
            data-testid="btn-submit-quote"
            className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 font-medium">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {isEdit ? 'Salva modifiche' : 'Crea preventivo'}
          </button>
        </div>
      </div>

      {graphicModal && (
        <GraphicRequestModal
          quoteId={quoteId}
          itemIndex={graphicModal.itemIndex}
          defaultProductName={graphicModal.defaultProductName}
          existingRequestId={graphicModal.existingRequestId}
          agentToken={agentToken}
          onClose={() => setGraphicModal(null)}
          onSaved={async (reqId) => {
            // ricarica lista graphic requests per aggiornare i badge
            try {
              const grRes = await fetch(`${API}/api/agent/graphic-requests?quote_id=${quoteId}`, {
                headers: { Authorization: `Bearer ${agentToken}` },
              });
              if (grRes.ok) {
                const gj = await grRes.json();
                const map = {};
                (gj.data || []).forEach((g) => { map[g.quote_item_index] = { id: g.id, status: g.status }; });
                setGraphicByIndex(map);
              }
            } catch { /* ignore */ }
          }}
        />
      )}
    </AgentLayout>
  );
};

export default AgentQuoteNew;
